import React, { useState, useEffect } from 'react';
import {
  DocumentPlusIcon,
  ChatBubbleLeftRightIcon,
  // ArrowPathIcon,
  BookOpenIcon,
  ScaleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/apiHelper';
import { callQueryLLM } from '../../utils/apiClient';
import { useUploadPreferences } from '../../contexts/UploadPreferencesContext';
import { validateFile, getAcceptedFileTypes, getFileTypeDescription, generateFileId } from '../../utils/uploadConfig';
import ChatInterface from '../../components/research/ChatInterface';
import DocumentManagement from '../../components/research/DocumentManagement';
import ZimLIISearchPanel from '../../components/research/ZimLIISearchPanel';
import ZimLIIQuickSearchModal from '../../components/research/ZimLIIQuickSearchModal';


const LegalResearchTab = () => {
  // Upload preferences
  const { preferences } = useUploadPreferences();

  // State management
  const [documents, setDocuments] = useState([]);
  const [activeDocuments, setActiveDocuments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showUploader, setShowUploader] = useState(false);

  const [showZimLIIModal, setShowZimLIIModal] = useState(false);
  const [zimLIIResults, setZimLIIResults] = useState(null);
  const [activeTab, setActiveTab] = useState('chat'); // 'chat', 'documents', 'zimlii'

  // Session management
  const [currentSession, setCurrentSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [pinnedMessages, setPinnedMessages] = useState([]);

  // Fetch data on component mount
  useEffect(() => {
    fetchDocuments();
    fetchSessions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getWelcomeMessage = () => ({
    id: 'welcome',
    type: 'ai',
    content: `Welcome to the **Legal Research Assistant**!

I can help you with:
• **Zimbabwean legal research** — cases, legislation, and statutory instruments
• **Document analysis** and legal document review  
• **Legal questions** and precedent research
• **Case preparation** and legal document drafting

Simply ask me any legal question or upload documents for analysis. Zimbabwe is our primary, fully indexed jurisdiction sourced from official judicial records and public legal repositories, with verifiable citations (source, date accessed). Additional African jurisdictions are on the expansion roadmap.

Get started by asking a legal question or uploading a document for review!`,
    timestamp: new Date()
  });

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/documents/');
      // Ensure response is always an array
      setDocuments(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError('Failed to load documents. Please try again.');
      // Set documents to empty array on error
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await apiRequest('/api/research/chat-sessions/');
      const sessionList = Array.isArray(response) ? response : [];
      setSessions(sessionList);

      // If there are existing sessions, set the most recent as current
      if (sessionList.length > 0) {
        const latestSession = sessionList[0]; // Sessions are ordered by -updated_at
        setCurrentSession(latestSession);
        // Load messages for this session
        if (latestSession.id) {
          loadSessionMessages(latestSession.id);
        }
      } else {
        // No sessions - show welcome message
        setMessages([getWelcomeMessage()]);
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      setSessions([]);
      // Show welcome message on error too
      setMessages([getWelcomeMessage()]);
    }
  };

  const loadSessionMessages = async (sessionId) => {
    try {
      const response = await apiRequest(`/api/research/chat-messages/?session=${sessionId}`);
      const rawMessages = Array.isArray(response) ? response : [];
      // Map backend role field to frontend type field
      const mappedMessages = rawMessages.map(msg => ({
        ...msg,
        type: msg.role === 'assistant' ? 'ai' : msg.role,
        references: msg.metadata?.references || [],
      }));
      setMessages(mappedMessages);

      // Also load session documents when loading a session
      await loadSessionDocuments(sessionId);
    } catch (err) {
      if (err.message && err.message.includes('Session not found')) {
        console.warn(`Session ${sessionId} not found or not accessible for messages`);
      } else {
        console.error('Failed to load session messages:', err);
      }
      setMessages([]);
    }
  };

  const loadSessionDocuments = async (sessionId) => {
    try {
      const response = await apiRequest(`/api/research/session-documents/by_session/?session=${sessionId}`);
      const sessionDocs = Array.isArray(response) ? response : [];

      // Extract full document objects from active session documents
      const activeDocuments = sessionDocs
        .filter(sessionDoc => sessionDoc.is_active && sessionDoc.document_name)
        .map(sessionDoc => ({
          id: sessionDoc.document,
          name: sessionDoc.document_name,
          // Include other document properties that might be available
          document_id: sessionDoc.document,
          created_at: sessionDoc.created_at,
          updated_at: sessionDoc.updated_at
        }));

      setActiveDocuments(activeDocuments);

      console.log(`Loaded ${activeDocuments.length} active documents for session ${sessionId}`);
    } catch (err) {
      // Handle specific error cases
      if (err.message && err.message.includes('Session not found')) {
        console.warn(`Session ${sessionId} not found or not accessible`);
      } else {
        console.error('Failed to load session documents:', err);
      }
      // Don't fail session loading, just clear active documents
      setActiveDocuments([]);
    }
  };

  const createNewSession = async () => {
    try {
      setLoading(true);
      const response = await apiRequest('/api/research/chat-sessions/', 'POST', {
        title: `Research Session ${new Date().toLocaleString()}`
      });

      if (response) {
        setSessions(prev => [response, ...prev]);
        setCurrentSession(response);
        setActiveDocuments([]); // Clear active documents for new session
        setMessages([
          {
            id: 'welcome-new',
            type: 'ai',
            content: 'New research session started. How can I assist you with your legal research today?',
            timestamp: new Date()
          }
        ]);
        return response;
      }
    } catch (err) {
      console.error('Failed to create new session:', err);
      setError('Failed to create new session. Please try again.');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const switchSession = async (sessionId) => {
    const session = sessions.find(s => s.id === sessionId);
    if (!session) return;
    setCurrentSession(session);
    if (session.id) {
      await loadSessionMessages(session.id);
    }
  };

  const deleteSession = async (sessionId) => {
    try {
      await apiRequest(`/api/research/chat-sessions/${sessionId}/`, 'DELETE');
      setSessions(prev => prev.filter(s => s.id !== sessionId));

      // If we deleted the current session, create a new one or switch to another
      if (currentSession && currentSession.id === sessionId) {
        const remainingSessions = sessions.filter(s => s.id !== sessionId);
        if (remainingSessions.length > 0) {
          switchSession(remainingSessions[0].id);
        } else {
          createNewSession();
        }
      }
    } catch (err) {
      console.error('Failed to delete session:', err);
      setError('Failed to delete session. Please try again.');
    }
  };

  const renameSession = async (sessionId, newTitle) => {
    try {
      const response = await apiRequest(`/api/research/chat-sessions/${sessionId}/rename/`, 'PATCH', {
        title: newTitle
      });
      if (response) {
        setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title: newTitle } : s));
        if (currentSession && currentSession.id === sessionId) {
          setCurrentSession(prev => ({ ...prev, title: newTitle }));
        }
      }
    } catch (err) {
      console.error('Failed to rename session:', err);
      setError('Failed to rename session. Please try again.');
    }
  };

  const pinMessage = async (messageId, note = '') => {
    try {
      const message = messages.find(m => m.id === messageId);
      if (message) {
        const pinnedMessage = {
          ...message,
          pinnedAt: new Date(),
          note: note
        };

        setPinnedMessages(prev => [...prev, pinnedMessage]);

        // Optionally save to backend
        if (currentSession) {
          await apiRequest(`/api/research/chat-sessions/${currentSession.id}/pin-message/`, 'POST', {
            message_id: messageId,
            note: note
          });
        }
      }
    } catch (err) {
      console.error('Failed to pin message:', err);
    }
  };

  const unpinMessage = async (messageId) => {
    try {
      setPinnedMessages(prev => prev.filter(m => m.id !== messageId));

      // Optionally remove from backend
      if (currentSession) {
        await apiRequest(`/api/research/chat-sessions/${currentSession.id}/unpin-message/`, 'POST', {
          message_id: messageId
        });
      }
    } catch (err) {
      console.error('Failed to unpin message:', err);
    }
  };

  const handleFileUpload = async (files) => {
    try {
      setLoading(true);
      const fileArray = Array.from(files);
      const validationErrors = [];
      const validFiles = [];

      // Validate each file using the new validation system
      fileArray.forEach(file => {
        const validation = validateFile(file, preferences);
        if (validation.isValid) {
          validFiles.push(file);
        } else {
          validationErrors.push(...validation.errors);
        }
      });

      // Show validation errors if any
      if (validationErrors.length > 0) {
        setError(validationErrors.join('. '));
        setLoading(false);
        return;
      }

      // If no valid files, return early
      if (validFiles.length === 0) {
        setError('No valid files to upload.');
        setLoading(false);
        return;
      }

      // Create a FormData object
      const formData = new FormData();

      // Add files to FormData with unique keys to prevent duplicate warnings
      validFiles.forEach((file, index) => {
        const fileId = generateFileId(file, Date.now() + index);
        formData.append(`file_${fileId}`, file);
      });

      // Upload the files
      const response = await apiRequest('/api/documents/upload/', 'POST', formData, {});

      // Update documents list with new uploads - ensure response is an array
      const uploadedDocs = Array.isArray(response) ? response : [];
      setDocuments(prev => [...(prev || []), ...uploadedDocs]);

      // Attach documents to current session if one exists
      if (currentSession && currentSession.id && uploadedDocs.length > 0) {
        try {
          // Create SessionDocument records to link documents to the current session
          const sessionDocumentPromises = uploadedDocs.map(doc =>
            apiRequest('/api/research/session-documents/', 'POST', {
              session: currentSession.id,
              document: doc.id
            })
          );

          await Promise.all(sessionDocumentPromises);
          console.log(`Successfully attached ${uploadedDocs.length} documents to session ${currentSession.id}`);
        } catch (sessionDocError) {
          console.error('Failed to attach documents to session:', sessionDocError);
          // Don't fail the upload process, just log the error
        }
      }

      // Auto-select uploaded documents if preference is enabled
      if (preferences.autoSelectUploadedDocuments) {
        setActiveDocuments(prev => [...prev, ...uploadedDocs.map(doc => ({
          id: doc.id,
          name: doc.name,
          document_id: doc.id,
          created_at: doc.created_at,
          updated_at: doc.updated_at
        }))]);
      }

      // Add system message about uploads
      const uploadMessage = {
        id: generateFileId({ name: 'upload-message', size: 0 }, Date.now()),
        type: 'system',
        content: `Uploaded ${validFiles.length} document(s). ${currentSession ? 'Documents attached to current session.' : ''} You can now reference them in your research.`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, uploadMessage]);
      setShowUploader(false);

      // Show analysis warning if preference is enabled and files support analysis
      if (preferences.showAnalysisWarning) {
        const analyzableFiles = validFiles.filter(file => {
          const validation = validateFile(file, preferences);
          return validation.canBeAnalyzed;
        });

        if (analyzableFiles.length > 0) {
          const analysisMessage = {
            id: generateFileId({ name: 'analysis-warning', size: 0 }, Date.now()),
            type: 'ai',
            content: `I can analyze ${analyzableFiles.length} of the uploaded files. Would you like me to analyze them for key insights?`,
            timestamp: new Date()
          };
          setMessages(prev => [...prev, analysisMessage]);
        }
      }

      // Return the uploaded documents for the chat interface
      return uploadedDocs;
    } catch (err) {
      console.error('Failed to upload documents:', err);
      setError('Failed to upload documents. Please try again.');

      // Add error message
      const errorMessage = {
        id: generateFileId({ name: 'error-message', size: 0 }, Date.now()),
        type: 'system',
        content: 'Failed to upload documents. Please try again.',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
      throw err; // Re-throw to let chat interface handle the error
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveDocument = async (docId) => {
    const wasActive = activeDocuments.some(doc => doc.id === docId);

    // Update local state first
    setActiveDocuments(prev => {
      if (prev.some(doc => doc.id === docId)) {
        return prev.filter(doc => doc.id !== docId);
      } else {
        // Find the full document object to add
        const fullDocument = documents.find(doc => doc.id === docId);
        if (fullDocument) {
          return [...prev, {
            id: fullDocument.id,
            name: fullDocument.name,
            document_id: fullDocument.id,
            created_at: fullDocument.created_at,
            updated_at: fullDocument.updated_at
          }];
        }
        return prev;
      }
    });

    // If we have a current session, update the session-document relationship
    if (currentSession && currentSession.id) {
      try {
        if (wasActive) {
          // Document was active, now deactivating - find SessionDocument and toggle it off
          const response = await apiRequest(`/api/research/session-documents/by_session/?session=${currentSession.id}`);
          const sessionDocs = Array.isArray(response) ? response : [];
          const sessionDoc = sessionDocs.find(sd => sd.document === docId);

          if (sessionDoc) {
            await apiRequest(`/api/research/session-documents/${sessionDoc.id}/toggle_active/`, 'PATCH');
            console.log(`Deactivated document ${docId} in session ${currentSession.id}`);
          }
        } else {
          // Document was not active, now activating - check if association exists
          const response = await apiRequest(`/api/research/session-documents/by_session/?session=${currentSession.id}`);
          const sessionDocs = Array.isArray(response) ? response : [];
          const existingSessionDoc = sessionDocs.find(sd => sd.document === docId);

          if (existingSessionDoc) {
            // Reactivate existing association
            await apiRequest(`/api/research/session-documents/${existingSessionDoc.id}/toggle_active/`, 'PATCH');
            console.log(`Reactivated document ${docId} in session ${currentSession.id}`);
          } else {
            // Create new association
            await apiRequest('/api/research/session-documents/', 'POST', {
              session: currentSession.id,
              document: docId
            });
            console.log(`Added document ${docId} to session ${currentSession.id}`);
          }
        }
      } catch (err) {
        console.error('Failed to update session document relationship:', err);
        // Revert local state change on error
        setActiveDocuments(prev => {
          if (wasActive) {
            // Re-add the document object
            const fullDocument = documents.find(doc => doc.id === docId);
            if (fullDocument) {
              return [...prev, {
                id: fullDocument.id,
                name: fullDocument.name,
                document_id: fullDocument.id,
                created_at: fullDocument.created_at,
                updated_at: fullDocument.updated_at
              }];
            }
            return prev;
          } else {
            return prev.filter(doc => doc.id !== docId);
          }
        });
      }
    }
  };

  const getActiveDocumentContext = () => {
    const activeDocIds = activeDocuments.map(doc => doc.id);
    const activeDocsDetails = (documents || []).filter(doc => activeDocIds.includes(doc.id));
    return activeDocsDetails.map(doc => ({
      id: doc.id,
      name: doc.name,
      content: doc.content || `Reference to document: ${doc.name}`
    }));
  };

  const handleSendMessage = async (message, files = []) => {
    if (!message.trim() && files.length === 0) return;

    // If files are provided, upload them first
    if (files.length > 0) {
      try {
        const uploadedDocs = await handleFileUpload(files);
        // Add the uploaded documents to active documents
        setActiveDocuments(prev => [...prev, ...uploadedDocs.map(doc => ({
          id: doc.id,
          name: doc.name,
          document_id: doc.id,
          created_at: doc.created_at,
          updated_at: doc.updated_at
        }))]);

        // Add system message about the uploads
        const uploadMessage = {
          id: `upload-${Date.now()}`,
          type: 'system',
          content: `Uploaded ${files.length} file(s): ${files.map(f => f.name).join(', ')}`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, uploadMessage]);
      } catch (err) {
        console.error('Failed to upload files:', err);
        const errorMessage = {
          id: `error-${Date.now()}`,
          type: 'system',
          content: 'Failed to upload files. Please try again.',
          timestamp: new Date(),
          isError: true
        };
        setMessages(prev => [...prev, errorMessage]);
        return;
      }
    }

    // Ensure we have a session
    let sessionToUse = currentSession;
    if (!sessionToUse) {
      try {
        sessionToUse = await createNewSession();
        if (!sessionToUse) {
          setError('Failed to create session. Please try again.');
          return;
        }
      } catch (err) {
        console.error('Failed to create new session:', err);
        setError('Failed to create new session. Please try again.');
        return;
      }
    }

    // Add user message to chat
    const userMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: message,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);

    try {
      setLoading(true);

      // Send message using the correct endpoint that handles both user message and AI response
      if (sessionToUse && sessionToUse.id) {
        const response = await apiRequest(`/api/research/chat-sessions/${sessionToUse.id}/send_message/`, 'POST', {
          content: message
        });

        // If we get back both user and AI messages, update our state
        if (response && response.ai_message) {
          const aiMessage = {
            id: response.ai_message.id,
            type: 'ai',
            content: response.ai_message.content,
            timestamp: new Date(response.ai_message.timestamp),
            references: response.ai_message.metadata?.references || []
          };

          setMessages(prev => [...prev, aiMessage]);

          // Update session title if backend auto-generated one
          if (response.session && response.session.title) {
            setCurrentSession(prev => ({ ...prev, title: response.session.title }));
            setSessions(prev => prev.map(s =>
              s.id === sessionToUse.id ? { ...s, title: response.session.title } : s
            ));
          }

          setLoading(false);
          return; // Exit early since we got the AI response from backend
        }
      }

      // Get active document context
      const documentContext = getActiveDocumentContext();

      // Get recent chat history (last 10 messages)
      const recentChat = messages.slice(-10);

      // Call LLM API through our FastAPI backend with proper system prompt
      const response = await callQueryLLM(message, documentContext, recentChat);

      // Add AI response to chat
      const aiMessage = {
        id: `ai-${Date.now()}`,
        type: 'ai',
        content: response.response || response,
        timestamp: new Date(),
        references: response.references || [],
        documentContext: documentContext.length > 0 ? documentContext.map(d => d.name) : undefined
      };

      setMessages(prev => [...prev, aiMessage]);

      // Save AI response to backend if we have a session
      if (currentSession) {
        await apiRequest(`/api/research/chat-sessions/${currentSession.id}/messages/`, 'POST', {
          content: aiMessage.content,
          role: 'assistant',
          metadata: {
            references: aiMessage.references,
            documentContext: aiMessage.documentContext
          }
        });
      }

    } catch (err) {
      console.error('Failed to get AI response:', err);

      // Add error message
      const errorMessage = {
        id: `error-${Date.now()}`,
        type: 'system',
        content: 'Failed to get AI response. Please try again.',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleZimLIISearch = async (searchQueryOrParams) => {
    // Handle both string query (from modal) and object params (from search panel)
    let searchQuery, searchParams;

    if (typeof searchQueryOrParams === 'string') {
      // Called from modal with simple string query
      searchQuery = searchQueryOrParams;
      searchParams = {
        query: searchQuery,
        jurisdiction: 'Zimbabwe',
        doc_type: '',
        date_from: '',
        date_to: '',
        page_size: 10
      };
    } else {
      // Called from search panel with parameters object
      searchParams = searchQueryOrParams;
      searchQuery = searchParams.query;

      // Ensure default values
      searchParams = {
        jurisdiction: 'Zimbabwe',
        doc_type: '',
        date_from: '',
        date_to: '',
        page_size: 10,
        ...searchParams
      };
    }

    if (!searchQuery || !searchQuery.trim()) return;

    try {
      setLoading(true);

      // System message about searching ZimLII
      const searchingMessage = {
        id: `zimlii-searching-${Date.now()}`,
        type: 'system',
        content: `🔍 Searching legal database for: "${searchQuery}"...`,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, searchingMessage]);

      // Query ZimLII API through our backend
      const response = await apiRequest('/api/research/queries/search-zimlii/', 'POST', searchParams);

      // Store results in state
      setZimLIIResults(response);

      // Create detailed results message
      let resultsContent = '';
      const resultCount = response.count || response.items?.length || 0;

      if (resultCount > 0) {
        resultsContent = `📚 **Found ${resultCount} relevant legal documents**\n\n`;

        if (response.items && response.items.length > 0) {
          // Categorize results
          const cases = response.items.filter(item => item.type === 'case' || item.type === 'judgment');
          const legislation = response.items.filter(item => item.type === 'legislation' || item.type === 'act');
          const other = response.items.filter(item => !['case', 'judgment', 'legislation', 'act'].includes(item.type));

          if (cases.length > 0) {
            resultsContent += `**📖 Cases (${cases.length}):**\n`;
            cases.slice(0, 3).forEach((item, index) => {
              resultsContent += `${index + 1}. **${item.title}**\n`;
              if (item.citation) resultsContent += `   Citation: ${item.citation}\n`;
              if (item.court) resultsContent += `   Court: ${item.court}\n`;
              if (item.date) resultsContent += `   Date: ${item.date}\n`;
              resultsContent += '\n';
            });
          }

          if (legislation.length > 0) {
            resultsContent += `**📜 Legislation (${legislation.length}):**\n`;
            legislation.slice(0, 3).forEach((item, index) => {
              resultsContent += `${index + 1}. **${item.title}**\n`;
              if (item.year) resultsContent += `   Year: ${item.year}\n`;
              resultsContent += '\n';
            });
          }

          if (other.length > 0) {
            resultsContent += `**📄 Other Documents (${other.length}):**\n`;
            other.slice(0, 2).forEach((item, index) => {
              resultsContent += `${index + 1}. **${item.title}**\n`;
              resultsContent += '\n';
            });
          }

          resultsContent += `\n💡 **Click the "Search Results" tab above to view full details and access all ${resultCount} results.**`;

          // Generate summary if we have results
          if (cases.length > 0 || legislation.length > 0) {
            resultsContent += `\n\n🔍 **Quick Summary:**\n`;
            resultsContent += `This search found ${cases.length} case${cases.length !== 1 ? 's' : ''} and ${legislation.length} piece${legislation.length !== 1 ? 's' : ''} of legislation related to "${searchQuery}". `;

            if (cases.length > 0) {
              resultsContent += `The cases include recent judgments that may provide relevant precedents. `;
            }

            if (legislation.length > 0) {
              resultsContent += `The legislation provides the statutory framework governing this area of law.`;
            }
          }
        }
      } else {
        resultsContent = `🔍 **No results found for "${searchQuery}"**\n\n`;
        resultsContent += `This search didn't return any matching legal documents. You might try:\n\n`;
        resultsContent += `• Using different search terms\n`;
        resultsContent += `• Searching for broader legal concepts\n`;
        resultsContent += `• Checking spelling and terminology\n`;
        resultsContent += `• Trying a different jurisdiction\n\n`;
        resultsContent += `💡 Zimbabwe has the most complete index, sourced from official judicial records and public legal repositories. Additional African jurisdictions are on the expansion roadmap.`;
      }

      // Add comprehensive results message
      const resultsMessage = {
        id: `zimlii-results-${Date.now()}`,
        type: 'zimlii',
        content: resultsContent,
        timestamp: new Date(),
        results: response,
        searchQuery: searchQuery
      };

      setMessages(prev => [...prev, resultsMessage]);

      // Switch to ZimLII results tab if there are results
      if (resultCount > 0) {
        setActiveTab('zimlii');

        // Add helpful message about the tab
        const tabMessage = {
          id: `zimlii-tab-info-${Date.now()}`,
          type: 'ai',
          content: `I've opened the Search Results tab where you can explore the full details of each document, read excerpts, and access the original sources. You can return to this chat anytime to discuss the findings or ask follow-up questions.`,
          timestamp: new Date(),
          isSystem: true
        };

        setTimeout(() => {
          setMessages(prev => [...prev, tabMessage]);
        }, 2000);
      }

    } catch (err) {
      console.error('Failed to search ZimLII:', err);

      // Add detailed error message
      const errorMessage = {
        id: `error-${Date.now()}`,
        type: 'system',
        content: `❌ **Search Failed**\n\nUnable to search legal database. This could be due to:\n• Network connectivity issues\n• Legal database service temporarily unavailable\n• Authentication problems\n\nPlease try again in a moment, or contact support if the issue persists.`,
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const deleteDocument = async (docId) => {
    try {
      setLoading(true);

      // Make API request to delete the document
      await apiRequest(`/api/documents/${docId}/`, 'DELETE');

      // Remove document from state
      setDocuments(prev => (prev || []).filter(doc => doc.id !== docId));

      // Remove from active documents if present
      setActiveDocuments(prev => (prev || []).filter(doc => doc.id !== docId));

      // Add system message about deletion
      const deleteMessage = {
        id: `delete-${Date.now()}`,
        type: 'system',
        content: 'Document deleted successfully.',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, deleteMessage]);
    } catch (err) {
      console.error('Failed to delete document:', err);

      // Add error message
      const errorMessage = {
        id: `error-${Date.now()}`,
        type: 'system',
        content: 'Failed to delete document. Please try again.',
        timestamp: new Date(),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="legal-research-tab h-full flex flex-col">
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-3 sm:mb-5">
        <div className="px-3 sm:px-6 py-3 sm:py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white flex items-center">
                <ScaleIcon className="h-5 w-5 sm:h-6 sm:w-6 mr-2 text-primary-500 flex-shrink-0" />
                <span className="truncate">Legal Research Assistant</span>
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                Upload documents, ask legal questions, and search across African jurisdictions.
              </p>
            </div>
            <div className="flex items-center">
              <button
                onClick={() => setShowZimLIIModal(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 shadow-sm"
              >
                <ScaleIcon className="h-4 w-4 mr-2" />
                Jurisdiction Search
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-t border-gray-200 dark:border-gray-700">
          <nav className="flex overflow-x-auto scrollbar-thin">
            <button
              onClick={() => setActiveTab('chat')}
              className={`px-4 sm:px-6 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'chat'
                  ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              <div className="flex items-center">
                <ChatBubbleLeftRightIcon className="h-5 w-5 mr-1" />
                Chat
              </div>
            </button>

            <button
              onClick={() => setActiveTab('documents')}
              className={`px-4 sm:px-6 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'documents'
                  ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
            >
              <div className="flex items-center">
                <DocumentPlusIcon className="h-5 w-5 mr-1" />
                Documents
                <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full px-2 py-0.5">
                  {documents.length}
                </span>
              </div>
            </button>

            {zimLIIResults && (
              <button
                onClick={() => setActiveTab('zimlii')}
                className={`px-4 sm:px-6 py-3 text-sm font-medium whitespace-nowrap ${activeTab === 'zimlii'
                    ? 'border-b-2 border-primary-500 text-primary-600 dark:text-primary-400'
                    : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                  }`}
              >
                <div className="flex items-center">
                  <BookOpenIcon className="h-5 w-5 mr-1" />
                  Search Results
                  {zimLIIResults.count > 0 && (
                    <span className="ml-2 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs rounded-full px-2 py-0.5">
                      {zimLIIResults.count}
                    </span>
                  )}
                </div>
              </button>
            )}
          </nav>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-grow bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <div className="h-full flex flex-col">
            <ChatInterface
              messages={messages}
              loading={loading}
              activeDocuments={activeDocuments}
              currentSession={currentSession}
              sessions={sessions}
              pinnedMessages={pinnedMessages}
              onSendMessage={handleSendMessage}
              onCreateSession={createNewSession}
              onSwitchSession={switchSession}
              onPinMessage={pinMessage}
              onUnpinMessage={unpinMessage}
              onDeleteSession={deleteSession}
              onRenameSession={renameSession}
              onUploadDocument={handleFileUpload}
            />
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="h-full flex flex-col">
            <DocumentManagement
              documents={documents}
              activeDocuments={activeDocuments}
              onUpload={handleFileUpload}
              onToggleActive={toggleActiveDocument}
              onDelete={deleteDocument}
              loading={loading}
              error={error}
            />
          </div>
        )}

        {/* ZimLII Results Tab */}
        {activeTab === 'zimlii' && zimLIIResults && (
          <div className="h-full flex flex-col">
            <ZimLIISearchPanel
              results={zimLIIResults}
              loading={loading}
              onSearch={handleZimLIISearch}
              onClose={() => setActiveTab('chat')}
            />
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded shadow-lg z-50">
          <div className="flex">
            <div className="py-1">
              <svg className="fill-current h-4 w-4 text-red-500 mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M2.93 17.07A10 10 0 1 1 17.07 2.93 10 10 0 0 1 2.93 17.07zm12.73-1.41A8 8 0 1 0 4.34 4.34a8 8 0 0 0 11.32 11.32zM9 11V9h2v6H9v-4zm0-6h2v2H9V5z" />
              </svg>
            </div>
            <div>
              <p className="font-bold">Error</p>
              <p className="text-sm">{error}</p>
            </div>
            <div className="pl-2">
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Document Upload Modal */}
      {showUploader && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 sm:top-20 mx-4 sm:mx-auto p-5 border w-auto sm:w-96 max-w-md shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Upload Documents
                </h3>
                <button
                  onClick={() => setShowUploader(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
                <input
                  type="file"
                  multiple
                  accept={getAcceptedFileTypes(preferences)}
                  onChange={(e) => {
                    if (e.target.files.length > 0) {
                      handleFileUpload(e.target.files);
                    }
                  }}
                  className="hidden"
                  id="file-upload"
                />
                <label
                  htmlFor="file-upload"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <DocumentPlusIcon className="h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                    {getFileTypeDescription(preferences)} (max {(preferences.maxFileSize / 1024 / 1024).toFixed(0)}MB each)
                  </p>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* ZimLII Quick Search Modal */}
      {showZimLIIModal && (
        <ZimLIIQuickSearchModal
          isOpen={showZimLIIModal}
          onClose={() => setShowZimLIIModal(false)}
          onSearch={handleZimLIISearch}
          loading={loading}
        />
      )}
    </div>
  );
};

export default LegalResearchTab;
